import DetailedTimeline from "@components/common/Timeline/DetailedTimeline";
import TransportTimeline from "@components/common/Timeline/TransportTimeline";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button, i18n, Icon, Skeleton } from "@thetreep/ui";
import { OfferChoices } from "./OfferChoices";
import { Solution } from "@gen/travel/v1/search_pb";
import { CabinClass } from "@pages/booking/search/Search";
import {
  availableCabinClassesWithLabels,
  getMoreOffers,
  isTrain,
} from "@services/travel";
import { DAP } from "@gen/dap/v1/dap_pb";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import { Offer, Transportation } from "@gen/travel/v1/common_pb";
import { ExchangeOffer, ExchangeSolution } from "@gen/travel/v1/exchange_pb";
import { localDateTimeToDate } from "@utils/date";

type OfferItemProps = {
  dap: DAP;
  solution: Solution | ExchangeSolution;
  cabinClass: CabinClass;
  listVisible: boolean;
  inward: boolean;
  transport: Transportation;
  onClassSelect: (cabinClass: CabinClass) => void;
  onConfirm: (offer: Offer | ExchangeOffer) => void;
  isFilteredOffer: (offer: Offer | ExchangeOffer) => boolean;
  isFilteredSolution: (
    solution: Solution | ExchangeSolution,
    cabinClass: CabinClass,
  ) => boolean;
  isConfirmLoading?: boolean;
};

export default function OfferItem({
  dap,
  solution,
  cabinClass,
  listVisible,
  inward,
  transport,
  onClassSelect,
  onConfirm,
  isFilteredSolution,
  isFilteredOffer,
  isConfirmLoading,
}: OfferItemProps) {
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const journey = solution.journey;
  const [selectedClass, setSelectedClass] = useState(cabinClass);
  const [showDetailsRoutes, setShowDetailsRoutes] = useState(false);
  const [offers, setOffers] = useState<Offer[] | ExchangeOffer[] | undefined>();
  const [selectedOffer, setSelectedOffer] = useState<Offer | ExchangeOffer>();
  const [isLoading, setIsLoading] = useState(false);
  const isPlane = transport == Transportation.PLANE;

  const onConfirmChoice = () => {
    if (!selectedOffer) {
      console.error("no offer selected");
      return;
    }
    onConfirm(selectedOffer);
  };

  const handleClassChange = (classType: CabinClass) => {
    setSelectedClass(classType);
    onClassSelect(classType);
  };

  function offersForCabinClass(
    offers: Offer[] | ExchangeOffer[],
    cabinClass: CabinClass,
  ) {
    return offers
      .filter((o) => o.cabinClass.value == cabinClass && isFilteredOffer(o))
      .sort(
        (a, b) =>
          Number(a.initialPrice?.amount) - Number(b.initialPrice?.amount),
      );
  }

  const [prevCabinClass, setPrevCabinClass] = useState(cabinClass);
  if (cabinClass !== prevCabinClass) {
    setPrevCabinClass(cabinClass);
    setSelectedClass(cabinClass);
  }

  const [prevOfferDeps, setPrevOfferDeps] = useState({ selectedClass, offers });
  if (
    offers &&
    (selectedClass !== prevOfferDeps.selectedClass ||
      offers !== prevOfferDeps.offers)
  ) {
    setPrevOfferDeps({ selectedClass, offers });
    setSelectedOffer(offersForCabinClass(offers, selectedClass)[0]);
  }

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setShowDetailsRoutes(false);
      // if the displayed result is train or outward, just display returned offers
      if (isTrain(solution) || !inward) {
        setOffers(solution.offers);
        setIsLoading(false);
        return;
      }
      // else if we are searching for return flights, use /more to get offers
      try {
        const res = await getMoreOffers(dap, solution);
        setSelectedOffer(undefined);
        setOffers(res.offers);
      } catch (e) {
        if (e instanceof ConnectError && e.rawMessage) {
          addToast("error", e.rawMessage);
        } else {
          console.error(e);
          addToast("error", t("search.errors.moreOffers"));
        }
      }
      setIsLoading(false);
    })();
  }, [solution]);

  if (!journey || !journey.schedule?.arrival) {
    return;
  }

  const formattedDate = Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(localDateTimeToDate(journey.schedule.arrival));

  function displayOffers() {
    if (isLoading) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <Skeleton animation width="100%" height="180px" />
        </div>
      );
    }
    if (!inward && transport == Transportation.PLANE) {
      return (
        <span className="text-text-strong text-sm">{t("search.moreMsg")}</span>
      );
    }
    if (!offers || !selectedOffer) {
      return <></>;
    }
    return (
      <OfferChoices
        solution={solution}
        offers={offers.filter(
          (o) => o.cabinClass.value == selectedClass && isFilteredOffer(o),
        )}
        selectedOffer={selectedOffer}
        setSelectedOffer={setSelectedOffer}
      />
    );
  }

  function displayCabinClasses() {
    const availableCabinClasses = availableCabinClassesWithLabels(solution, t)
      .filter((cc) => isFilteredSolution(solution, cc.cabinClass))
      .filter((cc) => offers?.some((o) => o.cabinClass.value == cc.cabinClass));

    if (availableCabinClasses.length === 0) {
      return null;
    }

    return (
      <div
        className={`flex justify-evenly button-group ${
          isPlane ? "2xl:hidden" : "xl:hidden"
        }`}
      >
        {availableCabinClasses.map((cc) => (
          <Button
            key={cc.label}
            size="mini"
            width="100%"
            kind={selectedClass === cc.cabinClass ? "primary" : "outline"}
            onClick={() => handleClassChange(cc.cabinClass)}
          >
            {t(cc.label)}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`border border-cnam-light-300 bg-white self-start rounded-lg p-6 lg:mt-10 w-full lg:min-w-[350px] lg:sticky lg:top-6 ${
        listVisible ? "hidden" : "block"
      }`}
    >
      <div className="text-gray-600 text-sm">{formattedDate}</div>
      {showDetailsRoutes ? (
        <>
          <div className="mt-4 flex flex-col gap-2">
            <DetailedTimeline journey={journey} type={"rail"} />
          </div>
          <button
            className="text-blue-600 mt-2"
            onClick={() => setShowDetailsRoutes(false)}
          >
            {t("results.seeLess")}
          </button>
        </>
      ) : (
        <>
          {journey.segments?.length > 1 ? (
            <>
              <TransportTimeline journey={journey} />
              <button
                className="text-strong text-sm mt-2"
                onClick={() => setShowDetailsRoutes(true)}
              >
                {t("results.seeDetails")}
              </button>
            </>
          ) : (
            <div className="text-strong text-sm mt-4">
              <DetailedTimeline journey={journey} type={"rail"} />
            </div>
          )}
        </>
      )}

      {isFilteredSolution(solution, selectedClass) && (
        <>
          <div className="mt-6">
            <div className="text-gray-800 text-center mb-4">
              {t("results.availableOffers")}
            </div>
            {displayCabinClasses()}
          </div>

          <div className="mt-4">{displayOffers()}</div>

          <div className="mt-6 flex [&_button]:w-[100%] md:[&_button]:w-[auto] justify-end">
            <Button
              onClick={onConfirmChoice}
              endEnhancer={<Icon color="white" src="arrow_right_line" />}
              isLoading={isConfirmLoading}
            >
              {t("results.confirmTravel")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
